import { type Config, config } from "@repo/config";
import type { Purchase } from "@repo/database/types";
import { isValidSubscriptionStatus } from "./plan-detection";
import { getTrialStatusForProperty } from "./trials";

const plans = config.payments.plans as Config["payments"]["plans"];

type PlanId = keyof typeof config.payments.plans;

/**
 * Explicit tier rank for `isAtLeastPlan`. This used to be the key order of
 * `config.payments.plans`, which put legacy `agencyCore` below `brand` and
 * `aiReportUnlock` above `enterprise`; inserting the v2 keys made that order
 * meaningless. Plans of one tier share a rank (v1 and v2 Brand, every Agency
 * Core), so a v2 subscriber passes every check its v1 counterpart passes.
 */
const PLAN_RANK: Record<PlanId, number> = {
	lifetime: 0,
	paygCredits: 1,
	aiReportUnlock: 1,
	brand: 10,
	brandV2: 10,
	agencyCore: 20,
	agencyCoreUsageBased: 20,
	agencyCoreV2: 20,
	agencyPro: 30,
	enterprise: 100,
};

export function isAtLeastPlan(userPlanId: string, requiredPlanId: string) {
	const userRank = PLAN_RANK[userPlanId as PlanId];
	const requiredRank = PLAN_RANK[requiredPlanId as PlanId];
	return (
		userRank !== undefined &&
		requiredRank !== undefined &&
		userRank >= requiredRank
	);
}
type PurchaseWithoutTimestamps = Omit<Purchase, "createdAt" | "updatedAt">;

export function getActivePlanFromPurchases(
	purchases?: PurchaseWithoutTimestamps[],
) {
	const subscriptionPurchase = purchases?.find(
		(purchase) => purchase.type === "SUBSCRIPTION",
	);

	if (subscriptionPurchase) {
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) => price.productId === subscriptionPurchase.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) => price.productId === subscriptionPurchase.productId,
			),
			status: subscriptionPurchase.status,
			purchaseId: subscriptionPurchase.id,
		};
	}

	const oneTimePurchase = purchases?.find(
		(purchase) => purchase.type === "ONE_TIME",
	);

	if (oneTimePurchase) {
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) => price.productId === oneTimePurchase.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) => price.productId === oneTimePurchase.productId,
			),
			status: "active",
			purchaseId: oneTimePurchase.id,
		};
	}

	const freePlan = Object.entries(plans).find(([_, plan]) => plan.isFree);

	return freePlan
		? {
				id: freePlan[0] as PlanId,
				status: "active",
			}
		: null;
}

export function createPurchasesHelper(
	purchases: PurchaseWithoutTimestamps[],
	property?: { verifiedAt?: Date | null },
) {
	const trial = property ? getTrialStatusForProperty(property) : null;
	if (trial) {
		return {
			activePlan: {
				id: trial.id,
				status: trial.status,
				remainingTrialDays: trial.remainingTrialDays,
			},
			hasSubscription: (planId: PlanId, opts?: { atLeast?: boolean }) => {
				if (!isValidSubscriptionStatus(trial.status)) return false;
				if (opts?.atLeast) return isAtLeastPlan(trial.id, planId);
				return trial.id === planId;
			},
			hasPurchase: (planId: PlanId) => {
				// Even when in trial, we should check for actual purchases
				return !!purchases?.some((purchase) =>
					Object.entries(plans)
						.find(([id]) => id === planId)?.[1]
						.prices?.some(
							(price) => price.productId === purchase.productId,
						),
				);
			},
		};
	}

	const activePlan = getActivePlanFromPurchases(purchases);

	const hasSubscription = (planId: PlanId, opts?: { atLeast?: boolean }) => {
		if (!activePlan || !isValidSubscriptionStatus(activePlan?.status ?? ""))
			return false;
		if (opts?.atLeast) return isAtLeastPlan(activePlan.id, planId);
		return activePlan.id === planId;
	};

	const hasPurchase = (planId: PlanId) => {
		return !!purchases?.some((purchase) =>
			Object.entries(plans)
				.find(([id]) => id === planId)?.[1]
				.prices?.some(
					(price) => price.productId === purchase.productId,
				),
		);
	};

	return {
		activePlan: activePlan
			? { ...activePlan, remainingTrialDays: undefined }
			: null,
		hasSubscription,
		hasPurchase,
	};
}

/**
 * Apply plan restrictions to time range and return query limit options
 *
 * NOTE: Bot tracking is now free for all plans. This function no longer applies
 * any time or event count restrictions. It's kept for backwards compatibility
 * but simply passes through the requested time range.
 *
 * @param purchases Array of purchases (unused, kept for backwards compatibility)
 * @param timeRange The requested time range
 * @param property Optional property object (unused, kept for backwards compatibility)
 * @returns Object with the original time range and no limits
 */
export function applyPlanLimits(
	purchases: PurchaseWithoutTimestamps[],
	timeRange: { start: Date | string; end: Date | string },
	property?: { verifiedAt?: Date | null },
) {
	// Bot tracking is now free - no restrictions applied
	return {
		isFreePlan: false,
		timeRange: {
			start: new Date(timeRange.start),
			end: new Date(timeRange.end),
		},
		limitClause: "",
	};
}
